import { listStaffVideoTemplates, upsertStaffVideoTemplate } from "~/api/staff"
import { isPositiveTemplateRank } from "~/common/videoTemplate/template"

export const loadStaffTemplateContents = async (
  signal?: AbortSignal,
): Promise<API.VideoTemplate.Content[]> => {
  const contents: API.VideoTemplate.Content[] = []
  const ids = new Set<string>()
  let total: number | undefined
  const readPage = async (
    page: number,
  ): Promise<API.VideoTemplate.Content[]> => {
    signal?.throwIfAborted()
    const { data } = await listStaffVideoTemplates(
      { page, page_size: 100 },
      signal,
    )
    if (
      !data ||
      !Array.isArray(data.items) ||
      !Number.isInteger(data.total) ||
      data.total < 0 ||
      (total !== undefined && data.total !== total)
    )
      throw new Error("模板列表已变化或格式无效，请重新加载")
    total = data.total
    for (const content of data.items) {
      const id = content?.templateId
      if (typeof id !== "string" || !id.trim() || ids.has(id))
        throw new Error("模板标识无效或重复，请检查模板数据")
      ids.add(id)
      contents.push(content)
    }
    if (contents.length === total) return contents
    if (!data.items.length || contents.length > total)
      throw new Error("模板列表分页数据不完整，请重新加载")
    return readPage(page + 1)
  }
  return readPage(0)
}

export const getNextTemplateRank = (
  contents: API.VideoTemplate.Content[],
): number => {
  const maximum = contents.reduce(
    (value, content) =>
      isPositiveTemplateRank(content.rank)
        ? Math.max(value, content.rank)
        : value,
    0,
  )
  const rank = Math.floor(maximum) + 1
  if (!Number.isSafeInteger(rank) || rank <= maximum)
    throw new Error("暂时无法追加模板，请先保存列表顺序后重试")
  return rank
}

export type TemplateOrder = {
  ids: string[]
  baseline: API.VideoTemplate.Content[]
  retry?: boolean
}

export const buildTemplateOrder = (
  contents: API.VideoTemplate.Content[],
  { ids, baseline, retry = false }: TemplateOrder,
): API.VideoTemplate.Content[] => {
  const current = new Map(
    contents.map((content) => [content.templateId, content]),
  )
  const previous = new Map(
    baseline.map((content) => [content.templateId, content]),
  )
  if (
    ids.length !== contents.length ||
    ids.length !== baseline.length ||
    new Set(ids).size !== ids.length ||
    current.size !== ids.length ||
    previous.size !== ids.length ||
    ids.some((id) => !current.has(id) || !previous.has(id))
  )
    throw new Error("模板列表已变化，请重新加载后调整顺序")
  return ids.flatMap((id, index) => {
    const content = current.get(id)!
    const rank = index + 1
    if (
      content.rank !== previous.get(id)!.rank &&
      !(retry && content.rank === rank)
    )
      throw new Error("模板顺序已被修改，请重新加载后调整")
    return content.rank === rank ? [] : [{ ...content, rank }]
  })
}

export const saveTemplateRanks = async (
  contents: API.VideoTemplate.Content[],
): Promise<void> => {
  if (contents.some((content) => !isPositiveTemplateRank(content.rank)))
    throw new Error("模板排序值必须大于 0")
  const saveBatch = async (offset: number): Promise<void> => {
    const batch = contents.slice(offset, offset + 4)
    if (!batch.length) return
    const results = await Promise.allSettled(
      batch.map(upsertStaffVideoTemplate),
    )
    const failure = results.find((result) => result.status === "rejected")
    if (failure) {
      const reason =
        failure.reason instanceof Error ? failure.reason.message : "请求失败"
      throw new Error(
        `顺序保存失败：${reason}。部分模板可能已更新，请重试保存；重新加载将采用服务器当前顺序。`,
      )
    }
    return saveBatch(offset + batch.length)
  }
  return saveBatch(0)
}

export const saveTemplateOrder = async (
  order: TemplateOrder,
): Promise<void> => {
  const contents = await loadStaffTemplateContents()
  return saveTemplateRanks(buildTemplateOrder(contents, order))
}
